import React, { useMemo, useRef, useState } from "react";
import {
  BUCKET_LABELS,
  BUCKET_ORDER,
  describeFollowUpReason,
} from "../hooks/useSmartView";
import {
  classNames,
  formatDueLabel,
  primaryContactEmail,
} from "./utils";
import SnoozePopover from "./SnoozePopover";

const BUCKET_ACCENT = {
  overdue: "bg-red-500",
  today: "bg-amber-500",
  tomorrow: "bg-sky-500",
  this_week: "bg-indigo-500",
  later: "bg-zinc-400",
};

function Bucket({ label, accent, items, children, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  if (!items?.length) return null;
  return (
    <div className="border-b border-zinc-200 last:border-b-0">
      <button
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2 px-5 py-2 text-left text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:bg-zinc-50"
      >
        <span className={`h-1.5 w-1.5 rounded-full ${accent}`} />
        <span>{label}</span>
        <span className="rounded-full bg-zinc-100 px-1.5 py-0.5 text-[10px] font-semibold tabular-nums text-zinc-700">
          {items.length}
        </span>
        <span className="ml-auto text-[10px] text-zinc-400">{open ? "Hide" : "Show"}</span>
      </button>
      {open ? <div>{children}</div> : null}
    </div>
  );
}

function FollowUpRow({
  row,
  selected,
  isActive,
  onToggle,
  onOpen,
  onSnooze,
  onSkipStep,
  onSendNow,
  hasQueueItem,
  gmailConnected,
}) {
  const snoozeAnchorRef = useRef(null);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const cl = row.campaignLead || {};
  const reason = describeFollowUpReason(row);
  const overdue =
    cl.nextActionAt && new Date(cl.nextActionAt).getTime() < Date.now();

  return (
    <div
      onClick={() => onOpen?.(row)}
      className={classNames(
        "grid cursor-pointer grid-cols-[32px,1.6fr,1.2fr,0.8fr,auto] items-center gap-3 border-b border-zinc-100 px-5 py-2.5 transition-colors",
        isActive ? "bg-indigo-50/60" : selected ? "bg-indigo-50/30" : "bg-white hover:bg-zinc-50"
      )}
    >
      <label onClick={(e) => e.stopPropagation()} className="flex items-center">
        <input
          type="checkbox"
          checked={selected}
          onChange={onToggle}
          className="h-3.5 w-3.5 rounded border-zinc-300 text-indigo-600"
        />
      </label>
      <div className="min-w-0">
        <p className="truncate text-sm font-semibold text-zinc-900">
          {row.lead?.company_name || "Untitled lead"}
        </p>
        <p className="truncate text-xs text-zinc-500">
          {primaryContactEmail(row.lead) || row.lead?.website || "No contact info"}
        </p>
      </div>
      <div>
        <span className="inline-flex items-center rounded-md bg-zinc-100 px-2 py-0.5 text-[11px] font-medium text-zinc-700">
          {reason}
        </span>
      </div>
      <div
        className={classNames(
          "text-xs tabular-nums",
          overdue ? "font-medium text-red-600" : "text-zinc-600"
        )}
      >
        {formatDueLabel(cl.nextActionAt)}
      </div>
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <div className="relative">
          <button
            ref={snoozeAnchorRef}
            onClick={() => setSnoozeOpen((v) => !v)}
            className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
          >
            Snooze
          </button>
          {snoozeOpen ? (
            <SnoozePopover
              anchorRef={snoozeAnchorRef}
              align="right"
              onSelect={(date) => onSnooze?.(row, date)}
              onClose={() => setSnoozeOpen(false)}
            />
          ) : null}
        </div>
        <button
          onClick={() => onSkipStep?.(row)}
          title="Advance to the next sequence step"
          className="rounded-md border border-zinc-200 bg-white px-2 py-1 text-[11px] font-medium text-zinc-600 hover:bg-zinc-50"
        >
          Skip step
        </button>
        {hasQueueItem ? (
          <button
            onClick={() => onSendNow?.(row)}
            disabled={!gmailConnected}
            className="rounded-md bg-zinc-900 px-2 py-1 text-[11px] font-medium text-white hover:bg-zinc-800 disabled:cursor-not-allowed disabled:bg-zinc-300"
          >
            Send now
          </button>
        ) : (
          <button
            onClick={() => onOpen?.(row)}
            className="rounded-md bg-indigo-600 px-2 py-1 text-[11px] font-medium text-white hover:bg-indigo-700"
          >
            Draft reply
          </button>
        )}
      </div>
    </div>
  );
}

export default function FollowUpsView({
  grouped,
  total,
  queueRows,
  onOpenLead,
  onSnooze,
  onSkipStep,
  onSendNow,
  onBulkSnooze,
  onBulkSkipStep,
  onBulkSendNow,
  gmailConnected,
  selectedRowId,
}) {
  const [selectedIds, setSelectedIds] = useState(new Set());

  const queueByCampaignLead = useMemo(() => {
    const map = new Map();
    for (const row of queueRows || []) {
      const id = row.campaignLead?._id || row.queueItem?.campaignLeadId;
      if (id) map.set(id, row);
    }
    return map;
  }, [queueRows]);

  const toggleOne = (id) => {
    setSelectedIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectedArray = useMemo(() => Array.from(selectedIds), [selectedIds]);

  if (!total) {
    return (
      <div className="flex h-full flex-col items-center justify-center px-6 text-center">
        <p className="text-base font-semibold text-zinc-900">No scheduled follow-ups</p>
        <p className="mt-1 max-w-sm text-sm text-zinc-500">
          Follow-ups appear here once leads are enrolled and their next action is scheduled. Import a list or enroll leads to begin.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col">
      {selectedIds.size ? (
        <div className="flex flex-wrap items-center gap-2 border-b border-indigo-100 bg-indigo-50/60 px-5 py-2 text-sm">
          <span className="font-medium text-indigo-900">{selectedIds.size} selected</span>
          <span className="h-4 w-px bg-indigo-200" />
          <button
            onClick={() => onBulkSnooze?.(selectedArray, 3)}
            className="rounded-md border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-900 hover:bg-indigo-100"
          >
            Snooze 3d
          </button>
          <button
            onClick={() => onBulkSnooze?.(selectedArray, 7)}
            className="rounded-md border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-900 hover:bg-indigo-100"
          >
            Snooze 7d
          </button>
          <button
            onClick={() => onBulkSkipStep?.(selectedArray)}
            className="rounded-md border border-indigo-200 bg-white px-2.5 py-1 text-xs font-medium text-indigo-900 hover:bg-indigo-100"
          >
            Move to next step
          </button>
          <button
            onClick={() => onBulkSendNow?.(selectedArray)}
            disabled={!gmailConnected}
            className="rounded-md bg-indigo-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:bg-zinc-300"
          >
            Send all now
          </button>
          <button
            onClick={() => setSelectedIds(new Set())}
            className="ml-auto text-xs font-medium text-indigo-700 hover:underline"
          >
            Clear selection
          </button>
        </div>
      ) : null}

      <div className="flex-1 overflow-y-auto">
        {BUCKET_ORDER.map((key) => (
          <Bucket
            key={key}
            label={BUCKET_LABELS[key]}
            accent={BUCKET_ACCENT[key]}
            items={grouped?.[key] || []}
            defaultOpen={key === "overdue" || key === "today" || key === "tomorrow"}
          >
            {(grouped?.[key] || []).map((row) => {
              const id = row.campaignLead?._id;
              const queueRow = queueByCampaignLead.get(id);
              return (
                <FollowUpRow
                  key={id}
                  row={row}
                  selected={selectedIds.has(id)}
                  isActive={selectedRowId === id}
                  onToggle={() => toggleOne(id)}
                  onOpen={onOpenLead}
                  onSnooze={onSnooze}
                  onSkipStep={onSkipStep}
                  onSendNow={
                    queueRow
                      ? () => onSendNow?.(queueRow.queueItem._id)
                      : null
                  }
                  hasQueueItem={Boolean(queueRow)}
                  gmailConnected={gmailConnected}
                />
              );
            })}
          </Bucket>
        ))}
      </div>
    </div>
  );
}
